import React, { useState, useReducer, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { NormalMatrixTable, FlippedMatrixTable } from "./RiskMatrixTable";
import { useToast } from "../hooks/use-toast";
import riskMatrixService from "../../services/riskMatrixService";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";

// Helper function to generate an empty matrix
const getEmptyMatrix = (rows, cols) => ({
  probabilities: Array(rows).fill(""),
  severities: Array(cols).fill(""),
  matrix: Array(rows)
    .fill()
    .map(() => Array(cols).fill("")),
  colors: {},
});

// Matrix reducer for state management
function matrixReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_MATRIX_DATA':
      return {...state, ...action.payload};
    case 'UPDATE_PROBABILITY':
      const newProbs = [...state.probabilities];
      newProbs[action.index] = action.value;
      return {...state, probabilities: newProbs};
    case 'UPDATE_SEVERITY':
      const newSevs = [...state.severities];
      newSevs[action.index] = action.value;
      return {...state, severities: newSevs};
    case 'UPDATE_CELL':
      const newMatrix = [...state.matrix];
      newMatrix[action.rowIndex][action.colIndex] = action.value;
      
      // Handle color transfer
      let newColors = {...state.colors};
      if (action.value && action.value !== action.prevValue) {
        newColors = {
          ...newColors,
          [action.value]: action.color || newColors[action.prevValue],
        };
      }
      return {...state, matrix: newMatrix, colors: newColors};
    case 'UPDATE_COLOR':
      return {
        ...state, 
        colors: {
          ...state.colors,
          [action.rating]: action.color
        }
      };
    case 'SET_MATRIX_SIZE':
      const newRows = action.rows;
      const newCols = action.cols;
      const currentRows = state.probabilities.length;
      const currentCols = state.severities.length;
      
      // If we're adding rows/columns
      if (newRows >= currentRows && newCols >= currentCols) {
        // Create new arrays with preserved existing values
        const newProbabilities = [...state.probabilities, ...Array(newRows - currentRows).fill("")];
        const newSeverities = [...state.severities, ...Array(newCols - currentCols).fill("")];
        
        // Create new matrix with preserved existing values
        const newMatrix = [...state.matrix.map(row => [...row, ...Array(newCols - currentCols).fill("")])];
        
        // Add additional rows if needed
        for (let i = 0; i < newRows - currentRows; i++) {
          newMatrix.push(Array(newCols).fill(""));
        }
        
        return {
          ...state,
          probabilities: newProbabilities,
          severities: newSeverities,
          matrix: newMatrix
        };
      } 
      // If we're reducing rows/columns
      else {
        return {
          ...state,
          probabilities: state.probabilities.slice(0, newRows),
          severities: state.severities.slice(0, newCols),
          matrix: state.matrix.slice(0, newRows).map(row => row.slice(0, newCols))
        };
      }
    case 'ADD_PROBABILITY':
      return {
        ...state,
        probabilities: [...state.probabilities, ""],
        matrix: [...state.matrix, new Array(state.severities.length).fill("")]
      };
    case 'ADD_SEVERITY':
      return {
        ...state,
        severities: [...state.severities, ""],
        matrix: state.matrix.map(row => [...row, ""])
      };
    case 'REMOVE_PROBABILITY':
      return {
        ...state,
        probabilities: state.probabilities.filter((_, i) => i !== action.index),
        matrix: state.matrix.filter((_, i) => i !== action.index)
      };
    case 'REMOVE_SEVERITY':
      return {
        ...state,
        severities: state.severities.filter((_, i) => i !== action.index),
        matrix: state.matrix.map(row => row.filter((_, i) => i !== action.index))
      };
    case 'REPLACE_MATRIX':
      return action.matrix;
    default:
      return state;
  }
}

export default function RiskMatrixEditorSteps({ initialMatrix = null, onSave, onCancel, existingMatrices = [] }) {
  const { toast } = useToast();
  
  // Breadcrumb navigation
  const [activeBreadcrumb, setActiveBreadcrumb] = useState(0);
  const [canGoNext, setCanGoNext] = useState(false);
  
  const breadcrumbs = [
    "Matrix Details",
    "Matrix Size",
    "Matrix Orientation",
    "Matrix Configuration"
  ];
  
  // Matrix configuration
  const [matrixName, setMatrixName] = useState(initialMatrix?.name || "New Risk Matrix");
  const [isAxesFlipped, setIsAxesFlipped] = useState(initialMatrix?.isAxesFlipped || false);
  const [selectedExistingMatrix, setSelectedExistingMatrix] = useState(null);
  
  // Matrix size settings
  const [rowCount, setRowCount] = useState(5);
  const [colCount, setColCount] = useState(5);
  const [nameError, setNameError] = useState("");
  
  // Matrix data state with reducer
  const [matrixData, dispatch] = useReducer(matrixReducer, null, () => {
    if (initialMatrix) {
      const matrix = typeof initialMatrix.matrix === "string"
        ? initialMatrix.matrix.split(";").map((row) => row.split("|"))
        : initialMatrix.matrix;

      return {
        probabilities: initialMatrix.probabilities,
        severities: initialMatrix.severities,
        matrix: matrix,
        colors: initialMatrix.colors || {},
      };
    }
    // Default empty 5x5 matrix
    return getEmptyMatrix(5, 5);
  });
  
  // Edit state
  const [editingCell, setEditingCell] = useState(null);
  const [editingCellValue, setEditingCellValue] = useState("");
  const [currentCellColor, setCurrentCellColor] = useState("");
  
  // Descriptions
  const [descriptions, setDescriptions] = useState({
    probabilities: initialMatrix?.descriptions?.probabilities || {},
    severities: initialMatrix?.descriptions?.severities || {},
  });
  
  // Diagonal text
  const [diagonalText, setDiagonalText] = useState({
    top: isAxesFlipped ? "Probability" : "Severity",
    bottom: isAxesFlipped ? "Severity" : "Probability",
  });
  
  // Handle diagonal text when axes are flipped
  useEffect(() => {
    if (isAxesFlipped) {
      setDiagonalText({
        top: "Probability",
        bottom: "Severity",
      });
    } else {
      setDiagonalText({
        top: "Severity",
        bottom: "Probability",
      });
    }
  }, [isAxesFlipped]);
  
  // Effect to handle selecting an existing matrix
  useEffect(() => {
    if (selectedExistingMatrix && existingMatrices.length > 0) {
      const selected = existingMatrices.find(matrix => matrix.id === selectedExistingMatrix);
      if (selected) {
        // Format matrix data properly
        const matrix = typeof selected.matrix === "string"
          ? selected.matrix.split(";").map((row) => row.split("|"))
          : selected.matrix;

        // Update matrix data state
        const newMatrixData = {
          probabilities: selected.probabilities,
          severities: selected.severities,
          matrix: matrix,
          colors: selected.colors || {},
        };
        
        dispatch({ type: 'REPLACE_MATRIX', matrix: newMatrixData });
        
        // Update other related states
        setMatrixName(selected.name);
        setDescriptions(selected.descriptions || { probabilities: {}, severities: {} });
        setDiagonalText(selected.diagonalText || { 
          top: isAxesFlipped ? "Probability" : "Severity", 
          bottom: isAxesFlipped ? "Severity" : "Probability" 
        });
        setIsAxesFlipped(selected.isAxesFlipped || false);
        
        // Skip to the last step when selecting an existing matrix
        setActiveBreadcrumb(3);
      }
    }
  }, [selectedExistingMatrix, existingMatrices, isAxesFlipped]);
  
  // Validate matrix name against existing matrices
  const validateMatrixName = (name) => {
    if (!name.trim()) {
      setNameError("Matrix name is required");
      return false;
    }
    
    // Check for duplicate names
    const isDuplicate = existingMatrices.some(
      matrix => matrix.name.toLowerCase() === name.toLowerCase() && 
                (!initialMatrix || matrix.id !== initialMatrix.id)
    );
    
    if (isDuplicate) {
      setNameError("A matrix with this name already exists");
      return false;
    }
    
    setNameError("");
    return true;
  };
  
  // Handle matrix name change
  const handleMatrixNameChange = (e) => {
    const newName = e.target.value;
    setMatrixName(newName);
    validateMatrixName(newName);
  };
  
  // Validate current step and update canGoNext
  useEffect(() => {
    switch (activeBreadcrumb) {
      case 0:
        setCanGoNext(matrixName.trim() !== "");
        break;
      case 1:
        setCanGoNext(rowCount >= 2 && colCount >= 2);
        break;
      case 2:
        setCanGoNext(true); // Always can go next from orientation step
        break;
      default:
        setCanGoNext(false);
    }
  }, [activeBreadcrumb, matrixName, rowCount, colCount]);
  
  // Apply matrix size when moving to next step
  const handleNextStep = () => {
    if (activeBreadcrumb === 0) {
      // Validate matrix name when moving from details step
      if (!validateMatrixName(matrixName)) {
        return;
      }
    }
    else if (activeBreadcrumb === 1) {
      // Apply matrix size when going from size step to orientation step
      dispatch({ 
        type: 'SET_MATRIX_SIZE', 
        rows: rowCount, 
        cols: colCount 
      });
    }
    
    setActiveBreadcrumb(prev => prev + 1);
  };
  
  // Update description
  const updateDescription = (type, index, value) => {
    setDescriptions(prevDescriptions => ({
      ...prevDescriptions,
      [type]: {
        ...prevDescriptions[type],
        [index]: value,
      },
    }));
  };
  
  // Handle save
  const handleSave = () => {
    // Create a copy of our matrix data for saving
    const matrixToSave = {
      name: matrixName,
      probabilities: matrixData.probabilities,
      severities: matrixData.severities,
      matrix: matrixData.matrix,
      colors: matrixData.colors,
      descriptions: {
        // Create a deep copy to avoid reference issues
        probabilities: { ...descriptions.probabilities },
        severities: { ...descriptions.severities }
      },
      diagonalText,
      isAxesFlipped
    };

    try {
      const validation = riskMatrixService.validateMatrixData(matrixToSave);
      if (!validation.isValid) {
        toast({
          variant: "destructive",
          title: "Invalid matrix data",
          description: validation.errors.join(", "),
        });
        return;
      }

      onSave(matrixToSave);
      
      toast({
        title: "Success",
        description: "Risk matrix saved successfully",
      });
    } catch (error) {
      console.error("Error saving matrix:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save matrix: " + error.message,
      });
    }
  };
  
  // Common props for matrix tables
  const sharedTableProps = {
    matrixData,
    descriptions,
    diagonalText,
    dispatch,
    editingCell,
    setEditingCell,
    editingCellValue,
    setEditingCellValue,
    currentCellColor,
    setCurrentCellColor,
    updateDescription
  };
  
  // Render step content based on active breadcrumb
  const renderStepContent = () => {
    switch (activeBreadcrumb) {
      case 0: // Matrix Details
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="matrix-name">Matrix Title</Label>
              <Input
                id="matrix-name"
                value={matrixName}
                onChange={handleMatrixNameChange}
                placeholder="Enter matrix title"
                className="mt-1"
              />
              {nameError && <p className="text-red-500 text-sm mt-1">{nameError}</p>}
            </div>
            
            {existingMatrices.length > 0 && (
              <div className="mt-4">
                <Label htmlFor="existing-matrix">Or select an existing matrix as template</Label>
                <Select 
                  onValueChange={(value) => setSelectedExistingMatrix(value)}
                  id="existing-matrix"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Existing Matrix" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingMatrices.map((matrix) => (
                      <SelectItem key={matrix.id} value={matrix.id}>
                        {matrix.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );
      
      case 1: // Matrix Size
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="row-count" className="text-lg font-medium">Number of rows:</Label>
              <div className="py-2">
                <Input
                  id="row-count"
                  type="number"
                  min={2}
                  max={10}
                  value={rowCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 2) {
                      setRowCount(value);
                    }
                  }}
                  className="w-full md:w-1/3"
                />
                {rowCount < 2 && (
                  <p className="text-red-500 text-sm mt-1">Must have at least 2 rows</p>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="col-count" className="text-lg font-medium">Number of columns:</Label>
              <div className="py-2">
                <Input
                  id="col-count"
                  type="number"
                  min={2}
                  max={10}
                  value={colCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 2) {
                      setColCount(value);
                    }
                  }}
                  className="w-full md:w-1/3"
                />
                {colCount < 2 && (
                  <p className="text-red-500 text-sm mt-1">Must have at least 2 columns</p>
                )}
              </div>
            </div>
          </div>
        );
      
      case 2: // Matrix Orientation
        return (
          <div className="space-y-4">
            <Label>Matrix Orientation</Label>
            <RadioGroup 
              defaultValue={isAxesFlipped ? "flipped" : "normal"}
              onValueChange={(val) => setIsAxesFlipped(val === "flipped")}
            >
              <div className="flex items-center space-x-2 mb-2">
                <RadioGroupItem value="normal" id="r1" />
                <Label htmlFor="r1">Probability Vertical / Severity Horizontal (Standard)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="flipped" id="r2" />
                <Label htmlFor="r2">Severity Vertical / Probability Horizontal (Alternate)</Label>
              </div>
            </RadioGroup>
          </div>
        );
      
      case 3: // Matrix Configuration
        return (
          <div className="space-y-4">
            <Label className="text-lg font-medium">Complete your Risk Matrix</Label>
            <p className="text-sm text-gray-500 mb-4">
              Click on any cell to edit values. Add descriptions to labels using the information icon.
            </p>
            
            <div className="overflow-x-auto">
              {isAxesFlipped ? (
                <FlippedMatrixTable {...sharedTableProps} />
              ) : (
                <NormalMatrixTable {...sharedTableProps} />
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Create Risk Matrix</CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="flex justify-center mb-6">
          <Breadcrumb>
            <BreadcrumbList className="text-base">
              {breadcrumbs.map((breadcrumb, index) => (
                <div className="grid grid-flow-col items-center" key={index}>
                  <BreadcrumbItem>
                    {activeBreadcrumb === index ? (
                      <BreadcrumbPage>{breadcrumb}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink>{breadcrumb}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>

                  {index < breadcrumbs.length - 1 && <BreadcrumbSeparator className="ml-3" />}
                </div>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        
        {renderStepContent()}
      </CardContent>
      
      <CardFooter className="mx-auto mt-4">
        <div className="w-full">
          <div className="grid grid-cols-2">
            <div className="col-span-1">
              {activeBreadcrumb > 0 && (
                <Button
                  className="min-w-[120px]"
                  variant="outline"
                  onClick={() => setActiveBreadcrumb(activeBreadcrumb - 1)}
                >
                  Previous
                </Button>
              )}
            </div>

            <div className="flex justify-end col-span-1">
              {activeBreadcrumb === breadcrumbs.length - 1 ? (
                <Button
                  className="min-w-[120px]"
                  onClick={handleSave}
                >
                  Save Matrix
                </Button>
              ) : (
                <Button
                  className="min-w-[120px]"
                  variant="outline"
                  disabled={!canGoNext}
                  onClick={handleNextStep}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}